import {
  ALL_BRANDS_URL_ID,
  EXCLUSIVES_CATEGORY_URL_ID,
  FEATURED_NEW_ARRIVALS_CATEGORY_URL_ID,
  RECENTLY_VIEWED_CATEGORY_URL_ID,
  JUST_FOR_YOU_CATEGORY_URL_ID,
  NEW_BRANDS_CATEGORY_URL_ID,
  TOP_SELLERS_CATEGORY_URL_ID,
  INTERNATIONAL_BEST_SELLERS_CATEGORY_URL_ID,
  BRAND_PROMOS_CATEGORY_URL_ID,
  ALL_EUROPEAN_BRANDS_URL_ID,
} from "@faire/retailer-shared/categories/urlId";
import { ICategory } from "@faire/web-api/indigofair/data/ICategory";
import allBrands from "@faire/web-api/routes/all-brands";
import { path as c1CategoryPath } from "@faire/web-api/routes/category/c1CategoryUrlId";
import flashSale from "@faire/web-api/routes/flash-sale";
import trendingOnFaire from "@faire/web-api/routes/trending-on-faire";
import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web/common/localization";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";

import { getCategoryDisplayName } from "@faire/retailer/services/Categories";
import { getSettingCommunityCategoryHeaderTokens } from "@faire/retailer/settings/getSettingCommunityCategoryHeaderTokens";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";

const RECOMMENDED_CATEGORIES = [
  JUST_FOR_YOU_CATEGORY_URL_ID,
  INTERNATIONAL_BEST_SELLERS_CATEGORY_URL_ID,
  TOP_SELLERS_CATEGORY_URL_ID,
  BRAND_PROMOS_CATEGORY_URL_ID,
  FEATURED_NEW_ARRIVALS_CATEGORY_URL_ID,
  RECENTLY_VIEWED_CATEGORY_URL_ID,
  EXCLUSIVES_CATEGORY_URL_ID,
  ALL_BRANDS_URL_ID,
];

export interface ICategoryLink {
  name: string;
  identifier: string;
  link: string;
  childLinks?: ICategoryLink[];
}
const useInjectedRecommendCategoryLinks = (): ICategoryLink[] => {
  const { strictLocalize } = useStrictLocalization();

  const { showFlashSales, showFlashSalesAsUnavailable } = useRetailerStore([
    "showFlashSales",
    "showFlashSalesAsUnavailable",
  ]);
  const showRedistributionEntryPoints =
    showFlashSales || showFlashSalesAsUnavailable;

  const injectedCategoryLinks = [];
  if (showRedistributionEntryPoints) {
    injectedCategoryLinks.push({
      identifier: "Sale",
      name: strictLocalize({
        defaultMessage: "Sale",
        description: {
          text: "Faire's redistribution channel, with timed offers (formely known as Flash Sales)",
        },
      }),
      link: flashSale(),
    });
  }
  injectedCategoryLinks.push({
    identifier: "Brands A-Z",
    name: strictLocalize({
      defaultMessage: "Brands A - Z",
      description: { text: "See all brands on Faire" },
    }),
    link: allBrands(),
  });

  injectedCategoryLinks.push({
    identifier: "Trending on Faire",
    name: strictLocalize({
      defaultMessage: "Trending on Faire",
      description: { text: "Products trending on Faire" },
    }),
    link: trendingOnFaire(),
  });

  return injectedCategoryLinks;
};

export function useRecommendedCategoryLinks(
  categories: ICategory[] | undefined = [],
  strictLocalize: StrictLocalizeFunction
): ICategoryLink[] {
  const injectedRecommndedCategoryLinks = useInjectedRecommendCategoryLinks();
  return categories
    .filter(
      ({ is_featured, path_identifier, url_id = "" }) =>
        is_featured &&
        path_identifier &&
        RECOMMENDED_CATEGORIES.includes(url_id)
    )
    .filter(
      ({ category_navigation_status }) =>
        category_navigation_status !== "INACTIVE"
    )
    .map((category) => {
      const { name = "", url_id = "" } = category;
      if (url_id === FEATURED_NEW_ARRIVALS_CATEGORY_URL_ID) {
        return {
          identifier: url_id,
          name: name,
          link: c1CategoryPath(NEW_BRANDS_CATEGORY_URL_ID),
        };
      }
      return {
        identifier: url_id,
        name: getCategoryDisplayName(name, strictLocalize),
        link: c1CategoryPath(url_id),
      };
    })
    .concat(injectedRecommndedCategoryLinks);
}

export function useSeasonalCollectionsCategoryLinks(
  categories: ICategory[] | undefined = []
): ICategoryLink[] {
  const { isQualifiedForEuropeFreeShipping } = useRetailerStore([
    "isQualifiedForEuropeFreeShipping",
  ]);
  return categories
    .filter(
      ({ is_featured, url_id = "" }) =>
        is_featured && !RECOMMENDED_CATEGORIES.includes(url_id)
    )
    .filter(
      ({ category_navigation_status }) =>
        category_navigation_status !== "INACTIVE"
    )
    .filter(({ header, featured_category_header_group }) => {
      if (isNotUndefined(featured_category_header_group)) {
        return (
          featured_category_header_group ===
          ICategory.FeaturedCategoryHeaderGroup.TRENDING_COLLECTIONS
        );
      }
      return !getSettingCommunityCategoryHeaderTokens().some(
        (token) => token === header?.token
      );
    })
    .filter(({ url_id }) => {
      if (
        url_id === ALL_EUROPEAN_BRANDS_URL_ID &&
        isQualifiedForEuropeFreeShipping
      ) {
        return false;
      }
      return true;
    })
    .map((category) => {
      return {
        identifier: category.url_id ?? "",
        name: category.name ?? "",
        link: c1CategoryPath(category.url_id ?? ""),
      };
    });
}

export function useCommunityCollectionsCategoryLinks(
  categories: ICategory[] | undefined = []
): ICategoryLink[] {
  return categories
    .filter(
      ({ is_featured, url_id = "", header, featured_category_header_group }) =>
        is_featured &&
        !RECOMMENDED_CATEGORIES.includes(url_id) &&
        (featured_category_header_group ===
          ICategory.FeaturedCategoryHeaderGroup.COMMUNITY ||
          getSettingCommunityCategoryHeaderTokens().some(
            (token) => token === header?.token
          ))
    )
    .filter(
      ({ category_navigation_status }) =>
        category_navigation_status !== "INACTIVE"
    )
    .map((category) => {
      return {
        identifier: category.url_id ?? "",
        name: category.name ?? "",
        link: c1CategoryPath(category.url_id ?? ""),
      };
    });
}
